import { DatalayerService } from '@analytics/datalayer';
import type { InteractionCategories, InteractionMap } from '@analytics/models';
import { AnalyticsUtils } from '@analytics/utils';
import {
  AfterContentInit,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Input,
  NgZone,
} from '@angular/core';

/**
 * Directive to track user interactions and send them to the data layer.
 *
 * The directive listens to a specified DOM event on the host element and
 * pushes the interaction data to the data layer.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[trackInteraction]',
  standalone: true,
})
export class TrackInteractionDirective<K extends InteractionCategories>
  implements AfterContentInit
{
  /**
   * Event name to listen for on the host element (e.g., 'click', 'mouseenter').
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input({ required: true }) trackInteraction!: keyof HTMLElementEventMap;

  /**
   * Interaction category key that determines the type of interaction data.
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input({ required: true }) interactionCategory!: K;

  /**
   * Interaction data corresponding to the interaction category.
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input({ required: true }) interactionData!: InteractionMap[K];

  readonly #datalayer = inject(DatalayerService);
  readonly #utils = inject(AnalyticsUtils);
  readonly #destroyRef = inject(DestroyRef);
  readonly #elRef = inject(ElementRef);
  readonly #ngZone = inject(NgZone);

  ngAfterContentInit() {
    // Use native addEventListener with capture phase
    // This ensures that nodes that have conflicting handlers attached to our `trackInteraction` flag
    // will not override the handler.
    this.#ngZone.runOutsideAngular(() => {
      const element = this.#elRef.nativeElement;
      const eventHandler = (event: Event) => {
        this.#ngZone.run(() => {
          this.trackEvent(event);
        });
      };

      // Use capture phase (true) to ensure this runs before Angular's handlers
      element.addEventListener(this.trackInteraction, eventHandler, true);

      // Clean up when directive is destroyed
      this.#destroyRef.onDestroy(() => {
        element.removeEventListener(this.trackInteraction, eventHandler, true);
      });
    });
  }

  async trackEvent(event: Event) {
    this.#datalayer.pushEvent({
      event: `${this.interactionCategory}-${event.type}`,
      info: {
        ...this.interactionData,
        placement: await this.#utils.getElementPlacement(
          this.interactionData.name
        ),
      },
    });
  }
}
